import {
  ComponentsContainer,
  ConfigurableFormItem,
  executeScript,
  getStyle,
  IToolboxComponent,
  useAvailableConstantsData,
  validateConfigurableComponentSettings,
} from "@shesha-io/reactjs";
import { IMapBoundaryProps } from "./model";
import { GlobalOutlined } from "@ant-design/icons";
import React, { useRef } from "react";
import Map from "../../components/mapping";
import { settingsForm } from "./settings";
import { LngLatLike } from "mapbox-gl";
import { nanoid } from "nanoid";
import { MapProvider } from "../../components/mapping/provider";
import { Modal, Input, Checkbox, Select, message } from "antd";
import { useHttpClient } from "@shesha-io/reactjs";
import { centroid } from "@turf/centroid";

// Replace previous getRandomColor with palette-based version
const colorPalette = [
  "#FF6633",
  "#FFB399",
  "#FF33FF",
  "#FFFF99",
  "#00B3E6",
  "#E6B333",
  "#3366E6",
  "#999966",
  "#99FF99",
  "#B34D4D",
  "#80B300",
  "#809900",
  "#E6B3B3",
  "#6680B3",
  "#66991A",
  "#FF99E6",
  "#CCFF1A",
  "#FF1A66",
  "#E6331A",
  "#33FFCC",
];
function getRandomColor() {
  return colorPalette[Math.floor(Math.random() * colorPalette.length)];
}

// Visible label style for the draw-save modal's fields (Input/Select placeholders
// alone aren't accessible labels once a value is entered). All fields share one
// grid (matching the "Add new input" modal), so the label column auto-sizes to
// whichever label is longest instead of a guessed fixed width, and every input
// still lines up in the same column.
const fieldsGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "max-content 1fr",
  columnGap: 12,
  rowGap: 12,
  alignItems: "center",
};

const fieldLabelStyle: React.CSSProperties = {
  textAlign: "left",
  fontWeight: 500,
  whiteSpace: "nowrap",
};

const fieldInputWrapperStyle: React.CSSProperties = {
  minWidth: 0,
};

// Enrich polygon with centroid
function enrichPolygonWithCenterAndArea(feature: any): void {
  if (feature.geometry?.type === "Polygon") {
    try {
      const c = centroid(feature as any);
      if (c && c.geometry && Array.isArray(c.geometry.coordinates)) {
        const [lng, lat] = c.geometry.coordinates;
        feature.properties.center = [lng, lat];
      }
    } catch (err) {
      // ignore centroid errors
    }
  }
}

const MapToolBox: IToolboxComponent<IMapBoundaryProps> = {
  type: "mapping",
  isInput: false,
  name: "Map",
  icon: <GlobalOutlined />,
  Factory: ({ model }) => {
    const allScriptArgs = useAvailableConstantsData();
    const drawRef = useRef<MapboxDraw | null>(null);
    const hiddenBtnRef = useRef<HTMLButtonElement | null>(null);
    const hiddenDeleteBtnRef = useRef<HTMLButtonElement | null>(null);
    const [mapKey, setMapKey] = React.useState(() => nanoid());
    const [modalOpen, setModalOpen] = React.useState(false);
    const [polygonName, setPolygonName] = React.useState("");
    const [pendingPolygon, setPendingPolygon] = React.useState<any | null>(
      null,
    );
    const [isRegisteredForVat, setIsRegisteredForVat] = React.useState(false);
    const [color, setColor] = React.useState("#4fb948");
    const [irrigationType, setIrrigationType] = React.useState<
      number | undefined
    >(undefined);
    // Use API to get irrigation type options
    const [irrigationTypeOptions, setIrrigationTypeOptions] = React.useState<
      { value: number; label: string }[]
    >([]);
    const httpClient = useHttpClient();
    const [owner, setOwner] = React.useState("");
    const [ownerOptions, setOwnerOptions] = React.useState([]);
    const [showOwner, setShowOwner] = React.useState(false);
    const [showVat, setShowVat] = React.useState(false);
    const [showIrrigationType, setShowIrrigationType] = React.useState(false);
    // Drives the "Name" field's label/placeholder so it reflects what's actually
    // being drawn, e.g. "Enter Field Name" vs "Enter Farm Name".
    const [entityLabel, setEntityLabel] = React.useState("");
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    // Expose hard reset action for configurable actions
    React.useEffect(() => {
      (window as any).hardResetMap = () => {
        setMapKey(nanoid());
      };
      return () => {
        delete (window as any).hardResetMap;
      };
    }, []);

    const getPanelStyle = {
      ...getStyle(model.panelStyle),
    };

    // Reverse-geocode a drawn boundary's centroid into a real street
    // address via Mapbox, falling back to no address on failure so callers
    // can fall back to the polygon name themselves.
    const reverseGeocodeCenter = async (
      lng: number,
      lat: number,
    ): Promise<string | undefined> => {
      try {
        const geocodeUrl = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${model.accessToken}&limit=1`;
        const geocodeResp = await fetch(geocodeUrl);
        const geocodeData = await geocodeResp.json();
        return geocodeData?.features?.[0]?.place_name;
      } catch (err) {
        console.warn("Reverse geocoding failed:", err);
        return undefined;
      }
    };

    const handleBoundaryDraw = async (polygons: any) => {
      // Runs here (not in handleModalSave) so both the modal-confirmed save
      // and the direct map "Save" action (wired via useMapActions/saveBtnRef,
      // which calls this without going through the modal) get an address
      // on the polygon before it reaches the onSave script (bug 113869).
      for (const polygon of polygons ?? []) {
        if (polygon?.properties?.address) continue;
        const [lng, lat] = polygon?.properties?.center ?? [];
        if (typeof lat === "number" && typeof lng === "number") {
          const placeName = await reverseGeocodeCenter(lng, lat);
          if (placeName) {
            polygon.properties.address = placeName;
          }
        }
      }

      return model.onSave
        ? await executeScript(model.onSave, {
            ...allScriptArgs,
            polygons,
          })
        : {};
    };

    const onGetBoundaries = async (): Promise<any> => {
      return model.onGetBoundaries
        ? await executeScript(model.onGetBoundaries, allScriptArgs)
        : [];
    };

    const onGetCenter = async (): Promise<LngLatLike> => {
      return model.onGetCenter
        ? await executeScript(model.onGetCenter, allScriptArgs)
        : [28.191795, -25.854708];
    };

    // Called after drawing a polygon (from Map)
    const handlePolygonDrawn = (polygon: any) => {
      setPendingPolygon(polygon);
      setPolygonName("");
      setIsRegisteredForVat(false); // Reset VAT checkbox
      setOwner(""); // Reset owner
      setColor(getRandomColor()); // Assign random color
      setIrrigationType(undefined); // Reset irrigation type
      // Determine which fields to show, and how to label the boundary being
      // drawn. Drawing a boundary on an entity's page creates its CHILD entity
      // (e.g. drawing on a Landholding page creates a Farm within it), so the
      // label reflects the child being created, not the current page's own entity.
      setShowOwner(false);
      setShowVat(false);
      setShowIrrigationType(false);
      setEntityLabel("");
      if (typeof window !== "undefined") {
        const path = window.location.pathname.toLowerCase();
        // Check most specific/deepest entity first: nested routes (e.g. a farm
        // page under a landholding, or a field page under a farm) contain the
        // parent entity's name too, so the parent must not shadow the child.
        if (path.includes("field-area") || path.includes("fieldarea")) {
          // Field Area is the leaf of the hierarchy - nothing further to create.
          setEntityLabel("Field Area");
        } else if (path.includes("field")) {
          // On a Field page, drawing creates a Field Area within it.
          setEntityLabel("Field Area");
        } else if (path.includes("farm")) {
          // On a Farm page, drawing creates a Field within it.
          setShowIrrigationType(true);
          setEntityLabel("Field");
        } else if (path.includes("landholding")) {
          // On a Landholding page, drawing creates a Farm within it.
          setShowOwner(true);
          setEntityLabel("Farm");
        }
      }
      setModalOpen(true);
    };

    const handleModalSave = async () => {
      if (isSubmitting) return;
      if (pendingPolygon) {
        setIsSubmitting(true);
        try {
          const polygonWithName = {
            ...pendingPolygon,
            properties: {
              ...pendingPolygon.properties,
              name: polygonName,
              color,
            },
          };
          // Enrich polygon with area and centroid before saving
          enrichPolygonWithCenterAndArea(polygonWithName);

          const irrigationTypeValue =
            typeof irrigationType === "number" ? irrigationType : null;
          try {
            const result = await handleBoundaryDraw([
              {
                ...polygonWithName,
                _ui: {
                  isRegisteredForVat,
                  owner,
                  irrigationType: irrigationTypeValue,
                },
              },
            ]);
            if (result !== false) {
              setMapKey(nanoid());
              setModalOpen(false);
              setPendingPolygon(null);
              setPolygonName("");
              setIsRegisteredForVat(false);
              setOwner("");
              setColor(getRandomColor());
              setIrrigationType(undefined);
            }
          } catch (error: any) {
            console.error("Failed to save polygon:", error);
            const backendMessage =
              error?.response?.data?.error?.validationErrors?.[0]?.message ||
              error?.response?.data?.error?.details ||
              error?.response?.data?.error?.message ||
              error?.message;
            message.error(
              backendMessage ||
                `Failed to create ${entityLabel || "boundary"} on the map. Please try again.`,
            );
          }
        } finally {
          setIsSubmitting(false);
        }
      } else {
        setModalOpen(false);
      }
    };

    const handleModalCancel = () => {
      setModalOpen(false);
      setPolygonName("");
      setPendingPolygon(null);
    };

    React.useEffect(() => {
      if (modalOpen) {
        httpClient
          .get("/api/dynamic/Shesha/Person/Crud/GetAll?properties=fullName")
          .then((data) => {
            const result = data?.data?.result || data;
            if (Array.isArray(result?.items)) {
              setOwnerOptions(
                result.items.map((item: any) => ({
                  value: item.fullName,
                  label: item.fullName,
                })),
              );
            } else {
              setOwnerOptions([]);
            }
          })
          .catch(() => setOwnerOptions([]));
        // Fetch irrigation type reference list from API
        httpClient
          .get(
            "/api/services/app/ReferenceList/GetByName?module=boxfusion.agrierp&name=Boxfusion.Agrierp.IrrigationType",
          )
          .then((data) => {
            const result = data?.data?.result || data;
            if (Array.isArray(result?.items)) {
              setIrrigationTypeOptions(
                result.items.map((item: any) => ({
                  value: item.itemValue,
                  label: item.item,
                })),
              );
            } else {
              setIrrigationTypeOptions([]);
            }
          })
          .catch(() => setIrrigationTypeOptions([]));
      }
    }, [modalOpen]);

    return (
      <ConfigurableFormItem model={model}>
        {(_value) => (
          <div>
            <MapProvider
              drawRef={drawRef}
              saveBtnRef={hiddenBtnRef}
              deleteBtnRef={hiddenDeleteBtnRef}
            >
              <Map
                key={mapKey}
                accessToken={model.accessToken}
                zoomLevel={model.zoomLevel}
                onSave={handleBoundaryDraw}
                onPolygonDrawn={handlePolygonDrawn}
                onGetBoundaries={onGetBoundaries}
                onGetCenter={onGetCenter}
                drawRef={drawRef}
                saveBtnRef={hiddenBtnRef}
                deleteBtnRef={hiddenDeleteBtnRef}
                enableCustomPanel={model.enableCustomPanel}
                enableSearch={model.enableSearch}
                panelPosition={model.panelPosition}
                searchbarPosition={model.searchbarPosition}
                panelStyle={getPanelStyle}
                boundaryColour={model.boundaryColour}
                mapStyle={model.mapStyle}
              >
                <ComponentsContainer
                  containerId={model.content?.id}
                  dynamicComponents={
                    model.isDynamic ? model?.content?.components : []
                  }
                />
              </Map>
              <Modal
                title="Enter the details"
                open={modalOpen}
                onOk={handleModalSave}
                onCancel={handleModalCancel}
                okText="Save"
                cancelText="Cancel"
                confirmLoading={isSubmitting}
                okButtonProps={{
                  disabled: isSubmitting,
                  style: { background: "#4fb948", borderColor: "#4fb948" },
                }}
              >
                <div style={fieldsGridStyle}>
                  <label htmlFor="polygonName" style={fieldLabelStyle}>
                    {entityLabel ? `${entityLabel} Name` : "Name"}:
                  </label>
                  <div style={fieldInputWrapperStyle}>
                    <Input
                      id="polygonName"
                      value={polygonName}
                      onChange={(e) => setPolygonName(e.target.value)}
                      onPressEnter={handleModalSave}
                      autoFocus
                    />
                  </div>
                  {/* Owner dropdown */}
                  {showOwner && (
                    <>
                      <label htmlFor="polygonOwner" style={fieldLabelStyle}>
                        Owner:
                      </label>
                      <div style={fieldInputWrapperStyle}>
                        <Select
                          id="polygonOwner"
                          value={owner}
                          onChange={setOwner}
                          options={ownerOptions}
                          style={{ width: "100%" }}
                          showSearch
                          optionFilterProp="label"
                        />
                      </div>
                    </>
                  )}
                  {/* VAT Registered checkbox */}
                  {showVat && (
                    <>
                      <label style={fieldLabelStyle}>VAT Registered:</label>
                      <div style={fieldInputWrapperStyle}>
                        <Checkbox
                          checked={isRegisteredForVat}
                          onChange={(e) =>
                            setIsRegisteredForVat(e.target.checked)
                          }
                        />
                      </div>
                    </>
                  )}
                  {/* IrrigationType dropdown */}
                  {showIrrigationType && (
                    <>
                      <label
                        htmlFor="polygonIrrigationType"
                        style={fieldLabelStyle}
                      >
                        Irrigation Type:
                      </label>
                      <div style={fieldInputWrapperStyle}>
                        <Select
                          id="polygonIrrigationType"
                          value={irrigationType}
                          onChange={(value) =>
                            setIrrigationType(
                              typeof value === "number" ? value : Number(value),
                            )
                          }
                          options={irrigationTypeOptions}
                          style={{ width: "100%" }}
                        />
                      </div>
                    </>
                  )}
                  {pendingPolygon &&
                    pendingPolygon.properties &&
                    typeof pendingPolygon.properties.area === "number" && (
                      <>
                        <label htmlFor="polygonArea" style={fieldLabelStyle}>
                          Area (ha):
                        </label>
                        <div style={fieldInputWrapperStyle}>
                          <Input
                            id="polygonArea"
                            readOnly
                            value={(
                              pendingPolygon.properties.area / 10000
                            ).toFixed(2)}
                          />
                        </div>
                      </>
                    )}
                </div>
              </Modal>
            </MapProvider>
          </div>
        )}
      </ConfigurableFormItem>
    );
  },
  initModel: (model) => ({
    ...model,
    accessToken: model.accessToken,
    content: { id: nanoid(), components: [] },
    hideLabel: true,
  }),
  settingsFormMarkup: settingsForm,
  validateSettings: (model) =>
    validateConfigurableComponentSettings(settingsForm, model),
  customContainerNames: ["content"],
};

export default MapToolBox;
